import { useEffect } from "react";
import { uniq } from "lodash";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useTipTapEditorContext } from "../TipTapEditorContext";
import { scrollToBottom } from "../utilities";
import type { Meta } from "./LinkItem";
import { LinkItem } from "./LinkItem";
import { MessageItem } from "./MessageItem";
import { useUnfurlLink } from "./useUnfurlLink";

interface LinkPreviewsProps {
  hasAttachments: boolean;
  isEditMode: boolean;
  linkMarks: any[];
  messageAttachments: any[];
  setHasLinks: (hasLinks: boolean) => void;
}

export const LinkPreviews = ({
  linkMarks,
  messageAttachments,
  hasAttachments,
  setHasLinks,
  isEditMode,
}: LinkPreviewsProps) => {
  const { shouldScrollIntoView } = useTipTapEditorContext();
  const linkHrefs = uniq(linkMarks.map(mark => mark?.attrs?.href));

  // Normalize URL by removing trailing slash for comparison
  const normalizeUrl = (url: string) => url?.replace(/\/$/, "");
  const normalizedLinkHrefs = linkHrefs.map(normalizeUrl);

  const { filteredUnfurledLinks, isLoading, isRemoveLinkPreviewsEnabled } =
    useUnfurlLink({
      linkHrefs: normalizedLinkHrefs,
      setHasLinks,
    });

  useEffect(() => {
    if (
      shouldScrollIntoView &&
      filteredUnfurledLinks.length > 0 &&
      !isLoading
    ) {
      scrollToBottom();
    }
  }, [filteredUnfurledLinks, isLoading, shouldScrollIntoView]);

  if (
    !(filteredUnfurledLinks.length || messageAttachments.length) ||
    isLoading
  ) {
    return null;
  }

  return (
    <>
      {hasAttachments && isEditMode && (
        <div className="border-primary mx-0.5 h-16 w-px rounded-lg border-l-2" />
      )}
      <div
        className={classNames("flex w-full gap-2 pb-2", {
          "flex-wrap": !hasAttachments,
          "flex-col": !isEditMode,
        })}
      >
        {messageAttachments.map(message => (
          <MessageItem key={message.id} message={message} />
        ))}
        {filteredUnfurledLinks.map(({ url, ...meta }: any) => (
          <LinkItem
            key={url}
            url={url}
            meta={meta as Meta}
            isRemoveLinkPreviewsEnabled={Boolean(isRemoveLinkPreviewsEnabled)}
          />
        ))}
      </div>
    </>
  );
};
